import React, {useEffect, useRef, useState} from 'react'
import {Box, Text, VStack, useMultiStyleConfig, Link, AspectRatio} from '@chakra-ui/react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {A11y, Thumbs} from 'swiper/modules'
import type SwiperType from 'swiper'
import {Picture, Video} from '_uiComponents'
import {CLDImageAsset, CLDVideoAsset} from '_types'
import {useAnalytics, usePageDesigner, useIsElementInViewport, useViewport} from '_customHooks'
import {updateCloudinaryPDImage, updateCloudinaryPDVideo, updateCloudinaryPDPoster, PAGE_DESIGNER_MODULES, createRedirectUrl} from '_utils'

export interface EditorialSliderItemData {
    element_type?: 'Image' | 'Video'
    cta_url?: string
    cld_image?: CLDImageAsset
    cld_video?: CLDVideoAsset
    media_ratio?: string
    title?: string
    content_color?: string
    content_t_align?: string
    content_h_align?: string
    content_v_align?: string
    description?: string
    focal_point_x?: string
}

export interface EditorialSliderItem {
    data?: EditorialSliderItemData
    id?: string
    typeId?: string
}

export interface EditorialSliderRegion {
    components?: EditorialSliderItem[]
    id?: string
}

export interface EditorialSliderProps {
    data: {
        section_title?: string
        class_name?: string
        custom_css?: string
    }
    regions: EditorialSliderRegion[]
    listingLayout?: string
    analyticsList?: string
    positionStart: number
    analyticsName?: string
    analyticsID?: string
}

// Slide width ratios as fractions of the Swiper container width — must match the CSS.
// These are used to compute the exact wrapper translate before the CSS transition fires,
// so the animation always moves toward the correct final position.
//
//   tablet  (768–1023 px) : active = 75 %, inactive = 32 %, left-aligned (no centering)
//   desktop (1024 px+)    : active = 50 %, inactive = 24 %, centered
//
// translate formula:
//   offset  = activeIndex × (container × inactiveRatio + spaceBetween)
//   desktop : translate = container/2 − offset − (container × activeRatio)/2
//   tablet  : translate = −offset
const getSlideWRatios = (isDesktop: boolean) =>
    isDesktop ? {activeRatio: 0.5, inactiveRatio: 0.24, centered: true} : {activeRatio: 0.75, inactiveRatio: 0.32, centered: false}

const calcWrapperTranslate = (swiper: SwiperType, isDesktop: boolean): number => {
    const {activeIndex, width: container} = swiper
    const spaceB = typeof swiper.params.spaceBetween === 'number' ? swiper.params.spaceBetween : 0
    const {activeRatio, inactiveRatio, centered} = getSlideWRatios(isDesktop)
    const offset = activeIndex * (container * inactiveRatio + spaceB)
    return centered ? container / 2 - offset - (container * activeRatio) / 2 : -offset
}

/**
 * applyWrapperTranslate Moves the Swiper wrapper to the calculated translate position.
 * - instant=true (resize, init): disables the CSS transition momentarily so the jump is imperceptible,
 *   forces a style flush via getBoundingClientRect, then re-enables the transition.
 * - instant=false (slide change): defers via requestAnimationFrame so Swiper's synchronous
 *   speed=0 cleanup (setTransition(0) → transitionDuration='0ms', fired both before and after
 *   onSlideChangeTransitionStart in the same JS frame) has already completed before we set the
 *   transition. Uses setProperty with !important for an additional layer of safety.
 */
const applyWrapperTranslate = (swiper: SwiperType, instant: boolean, isDesktop: boolean): void => {
    if (instant) {
        const translate = calcWrapperTranslate(swiper, isDesktop)
        swiper.wrapperEl.style.setProperty('transition', 'none', 'important')
        swiper.wrapperEl.style.transform = `translate3d(${translate}px, 0px, 0px)`
        swiper.wrapperEl.getBoundingClientRect() // force flush styles
        swiper.wrapperEl.style.removeProperty('transition')
    } else {
        const translate = calcWrapperTranslate(swiper, isDesktop) // compute now — activeIndex is correct
        requestAnimationFrame(() => {
            if (swiper.destroyed) return
            swiper.wrapperEl.style.setProperty('transition', 'transform 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94)', 'important')
            swiper.wrapperEl.style.transform = `translate3d(${translate}px, 0px, 0px)`
        })
    }
}
/**
 * addMobilePortraitCrop Inserts a chained Cloudinary transformation in the URL for mobile portrait crop
 * (327:400). It should match the CSS aspect-ratio of the slide on mobile.
 * - c_fill → crops + resizes to cover the target frame
 * - g_auto → Cloudinary AI automatically selects the best crop
 * - ar_327:400 → target portrait ratio
 */
const addMobilePortraitCrop = (url: string): string => {
    if (!url) return url
    if (/\/v\d+\//.test(url)) {
        return url.replace(/(\/v\d+\/)/, '/c_fill,g_auto,ar_327:400$1')
    }
    return url.replace(/\/([^/]+)$/, '/c_fill,g_auto,ar_327:400/$1')
}

const EditorialSlider: React.FC<EditorialSliderProps> = ({data, regions, ...rest}) => {
    const {section_title, class_name, custom_css} = data ?? {}
    const styles = useMultiStyleConfig('EditorialSlider', {}) as Record<string, any>
    const swiperRef = useRef<SwiperType | null>(null)
    const targetElement = useRef<HTMLDivElement>(null)
    const {isMobile, isDesktop} = useViewport()
    const {analyticsEventPromoView, analyticsEventPromoClick} = useAnalytics()
    const isInViewport = useIsElementInViewport(targetElement)
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null)
    const {globalImageTransformation, globalVideoTransformation, CloudinaryPageDesignerCNAME, linkObject} = usePageDesigner()

    const [alreadySentPromoView, setAlreadySentPromoView] = useState(false)

    // Re-apply instant centering whenever isDesktop changes (e.g. after viewport hydration).
    // onSwiper fires once at init and captures isDesktop via closure — if isDesktop was still
    // false at that point, this effect corrects the wrapper position once the value is known.
    useEffect(() => {
        const swiper = swiperRef.current
        if (!swiper || swiper.destroyed || swiper.params.slidesPerView !== 'auto') return
        if (!getSlideWRatios(isDesktop).centered) return
        requestAnimationFrame(() => {
            if (!swiperRef.current || swiperRef.current.destroyed) return
            applyWrapperTranslate(swiperRef.current, true, isDesktop)
        })
    }, [isDesktop])

    useEffect(() => {
        if (!alreadySentPromoView && isInViewport) {
            analyticsEventPromoView({
                id: rest.analyticsID ? rest.analyticsID : 'null',
                position: rest.positionStart,
                creative: PAGE_DESIGNER_MODULES.EDITORIAL_SLIDER,
                name: rest.analyticsName ? rest.analyticsName : 'null',
            })
            setAlreadySentPromoView(true)
        }
    }, [isInViewport, alreadySentPromoView])

    const items = regions?.[0]?.components ?? []

    return (
        <VStack
            {...styles.wrapper}
            className={class_name}
            data-testid="editorial-slider"
            sx={custom_css ? {[`& .editorial-slider-root`]: {}} : undefined}
            ref={targetElement}
        >
            {section_title && (
                <Text {...styles.title} className="cc-title">
                    {section_title}
                </Text>
            )}

            {items.length > 0 && (
                <Box sx={styles.titleNav}>
                    <Swiper
                        className="editorial-slider-title-nav"
                        modules={[Thumbs]}
                        slidesPerView="auto"
                        spaceBetween={40}
                        watchSlidesProgress
                        breakpoints={{
                            0: {centeredSlides: true, allowTouchMove: true},
                            768: {centeredSlides: false, allowTouchMove: false},
                        }}
                        onSwiper={(swiper) => {
                            setThumbsSwiper(swiper)
                        }}
                    >
                        {items.map((item, idx) => (
                            <SwiperSlide key={`title-${item.id ?? idx}`} className="editorial-slider-title-nav-slide">
                                {item.data?.title ?? ''}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Box>
            )}

            <Box sx={styles.swiperWrapper}>
                <Swiper
                    modules={[A11y, Thumbs]}
                    thumbs={{swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null}}
                    className="editorial-slider-swiper"
                    autoHeight
                    breakpoints={{
                        0: {slidesPerView: 1.1, centeredSlides: true, spaceBetween: 8, speed: 300},
                        768: {slidesPerView: 'auto', centeredSlides: true, spaceBetween: 8, speed: 0, virtualTranslate: true},
                        1024: {slidesPerView: 'auto', centeredSlides: true, spaceBetween: 8, speed: 0, virtualTranslate: true},
                    }}
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper
                        if (swiper.params.slidesPerView === 'auto') {
                            requestAnimationFrame(() => {
                                // Desktop: center the first slide instantly (virtualTranslate keeps Swiper from overriding the transform).
                                // Tablet: translateX(0) is already correct for left-aligned layout, nothing to do.
                                if (getSlideWRatios(isDesktop).centered) applyWrapperTranslate(swiper, true, isDesktop)
                            })
                        }
                    }}
                    onSlideChange={(swiper) => {
                        thumbsSwiper?.slideTo(swiper.activeIndex)
                    }}
                    onSlideChangeTransitionStart={(swiper) => {
                        if (swiper.params.slidesPerView !== 'auto') return
                        applyWrapperTranslate(swiper, false, isDesktop) // animated by CSS transition
                    }}
                >
                    {items.map((item, idx) => {
                        const {cld_image, cld_video, element_type, title, focal_point_x, description, cta_url, media_ratio} = item.data ?? {}
                        const isVideo = element_type === 'Video' && !!cld_video

                        const videoUrl = isVideo ? updateCloudinaryPDVideo(cld_video, globalVideoTransformation, CloudinaryPageDesignerCNAME) : ''
                        const posterUrl = isVideo ? updateCloudinaryPDPoster(cld_video, globalVideoTransformation, CloudinaryPageDesignerCNAME) : ''

                        const mobileVideoUrl = addMobilePortraitCrop(videoUrl)
                        const activeVideoUrl = isVideo ? (isMobile ? mobileVideoUrl : videoUrl) : videoUrl
                        const imgUrl = !isVideo && cld_image ? updateCloudinaryPDImage(cld_image, globalImageTransformation, CloudinaryPageDesignerCNAME) : ''

                        const mobileImgUrl = addMobilePortraitCrop(imgUrl)

                        const slideContent = (
                            <>
                                <Box className="editorial-slider-crop">
                                    {isVideo ? (
                                        <Box
                                            className="editorial-slider-picture"
                                            position="relative"
                                            left="50%"
                                            width={{base: '100%', md: '75vw', lg: '50vw'}}
                                            height="auto"
                                        >
                                            {!isMobile && media_ratio ? (
                                                <AspectRatio ratio={Number(media_ratio)}>
                                                    <Video
                                                        videolg={activeVideoUrl}
                                                        videoxs={activeVideoUrl}
                                                        poster={posterUrl}
                                                        objectFit="cover"
                                                        autoplay
                                                        muted
                                                        loop
                                                        width="100%"
                                                        height="100%"
                                                    />
                                                </AspectRatio>
                                            ) : (
                                                <Video
                                                    videolg={activeVideoUrl}
                                                    videoxs={activeVideoUrl}
                                                    poster={posterUrl}
                                                    objectFit="cover"
                                                    autoplay
                                                    muted
                                                    loop
                                                    width="100%"
                                                    height="100%"
                                                />
                                            )}
                                        </Box>
                                    ) : (
                                        <Box
                                            className="editorial-slider-picture"
                                            position="relative"
                                            left="50%"
                                            width={{base: '100%', md: '75vw', lg: '50vw'}}
                                            height="auto"
                                        >
                                            {!isMobile && media_ratio ? (
                                                <AspectRatio ratio={Number(media_ratio)}>
                                                    <Picture
                                                        imagexl={imgUrl}
                                                        imagelg={imgUrl}
                                                        imagemd={imgUrl}
                                                        imagesm={mobileImgUrl}
                                                        imagexs={mobileImgUrl}
                                                        objectFit="cover"
                                                        alt={title ?? ''}
                                                        width="100%"
                                                        height="100%"
                                                        hasLazy={false}
                                                    />
                                                </AspectRatio>
                                            ) : (
                                                <Picture
                                                    imagexl={imgUrl}
                                                    imagelg={imgUrl}
                                                    imagemd={imgUrl}
                                                    imagesm={mobileImgUrl}
                                                    imagexs={mobileImgUrl}
                                                    alt={title ?? ''}
                                                    objectFit="cover"
                                                    width="100%"
                                                    height="100%"
                                                    hasLazy={false}
                                                />
                                            )}
                                        </Box>
                                    )}
                                </Box>
                                <Text className="editorial-slider-description">{description ?? ''}</Text>
                            </>
                        )

                        return (
                            <SwiperSlide
                                key={item.id ?? idx}
                                className="editorial-slider-slide"
                                style={{'--focal-x': (focal_point_x ?? 50) + '%'} as React.CSSProperties}
                            >
                                {cta_url ? (
                                    <Box
                                        as={Link}
                                        href={createRedirectUrl(cta_url, linkObject.hostlink, linkObject.locale, linkObject.site)}
                                        display="flex"
                                        flexDirection="column"
                                        w="100%"
                                        textDecoration="none"
                                        _hover={{textDecoration: 'none'}}
                                        onClick={() =>
                                            analyticsEventPromoClick({
                                                id: rest.analyticsID ? rest.analyticsID : 'null',
                                                position: String(rest.positionStart) + '-' + String(idx + 1),
                                                creative: PAGE_DESIGNER_MODULES.EDITORIAL_SLIDER,
                                                promoText: description ?? '',
                                                name: rest.analyticsName ? rest.analyticsName : 'null',
                                            })
                                        }
                                    >
                                        {slideContent}
                                    </Box>
                                ) : (
                                    slideContent
                                )}
                            </SwiperSlide>
                        )
                    })}
                </Swiper>
            </Box>
        </VStack>
    )
}

export {EditorialSlider}
